import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { registrarAcao } from '@/shared/utils/reportAction';
import { reportError } from '@/shared/utils/reportError';
import { comandosDaSuperficie, FalhaDaSimulacao } from '../store/services';
import type { DocumentoDaAssinatura, SuperficieDaSimulacao } from '../types';
import type { LeituraDoDocumento, DocumentoNaTela } from '../types/hookTypes';
import { classificarLink } from '../utils/classificarLink';
const FALHA_DO_VISOR = 'Visor não exibiu o documento de teste da assinatura';

/**
 * Hook de efeito de navegador (exceção escrita da regra #1), como o de abrir arquivo da conversa:
 * a URL de leitura vale 60 s e é credencial, então vive só aqui e nunca passa pelo Redux (CA-18).
 * Cada tentativa pede um acesso novo. 404 é o arquivo que não existe ou não é desta simulação.
 */
export function useDocumentoDaAssinatura(superficie, simulacaoId, arquivoId) {
  const $ = _c(18);
  const [tentativa, setTentativa] = useState(0);
  const [leitura, setLeitura] = useState(null);
  const chave = simulacaoId && arquivoId ? `${simulacaoId}/${arquivoId}/${tentativa}` : null;
  let t0;
  let t1;
  if ($[0] !== arquivoId || $[1] !== chave || $[2] !== simulacaoId || $[3] !== superficie) {
    t0 = () => {
      if (!chave || !simulacaoId || !arquivoId) {
        return;
      }
      let vigente = true;
      const ids = {
        simulacaoId,
        arquivoId
      };
      const concluir = resultado => {
        if (vigente) {
          setLeitura(resultado);
        }
      };
      comandosDaSuperficie(superficie).acessarArquivo(simulacaoId, arquivoId).then(t2 => {
        const {
          url,
          expiraEm
        } = t2;
        const destino = classificarLink(url);
        if (destino?.safe) {
          concluir({
            chave,
            status: "pronto",
            url: destino.href,
            expiraEm
          });
          return;
        }
        reportError(new Error("URL do documento da assinatura fora de https"), {
          source: "negociacaoSimulacao",
          acao: "documentoDaAssinatura",
          ...ids
        });
        concluir({
          chave,
          status: "erro"
        });
      }, erro => {
        if (erro instanceof FalhaDaSimulacao && erro.erro.status === 404) {
          registrarAcao("negociacaoSimulacao.arquivoIndisponivel", ids);
          concluir({
            chave,
            status: "indisponivel"
          });
          return;
        }
        reportError(erro instanceof Error ? erro : new Error("Falha ao abrir o documento"), {
          source: "negociacaoSimulacao",
          acao: "documentoDaAssinatura",
          ...ids
        });
        concluir({
          chave,
          status: "erro"
        });
      });
      return () => {
        vigente = false;
      };
    };
    t1 = [arquivoId, chave, simulacaoId, superficie];
    $[0] = arquivoId;
    $[1] = chave;
    $[2] = simulacaoId;
    $[3] = superficie;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setTentativa(_temp);
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const tentarDeNovo = t2;
  let t3;
  if ($[7] !== arquivoId || $[8] !== chave || $[9] !== leitura || $[10] !== simulacaoId) {
    t3 = () => {
      if (leitura?.status !== "pronto" || leitura.chave !== chave) {
        return;
      }
      const venceu = Date.now() >= Date.parse(leitura.expiraEm);
      if (!venceu) {
        reportError(new Error(FALHA_DO_VISOR), {
          source: "negociacaoSimulacao",
          acao: "documentoDaAssinatura",
          simulacaoId,
          arquivoId
        });
      }
      setLeitura({
        chave: leitura.chave,
        status: venceu ? "expirado" : "erro"
      });
    };
    $[7] = arquivoId;
    $[8] = chave;
    $[9] = leitura;
    $[10] = simulacaoId;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const registrarFalhaDoVisor = t3;
  let t4;
  if ($[12] !== chave || $[13] !== leitura) {
    t4 = !chave ? {
      status: "indisponivel"
    } : leitura?.chave !== chave ? {
      status: "carregando"
    } : leitura.status === "pronto" ? {
      status: "pronto",
      url: leitura.url
    } : {
      status: leitura.status
    };
    $[12] = chave;
    $[13] = leitura;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const documento = t4;
  let t5;
  if ($[15] !== documento || $[16] !== registrarFalhaDoVisor) {
    t5 = {
      documento,
      tentarDeNovo,
      registrarFalhaDoVisor
    };
    $[15] = documento;
    $[16] = registrarFalhaDoVisor;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}
function _temp(atual) {
  return atual + 1;
}